import axios from "axios";
import config from "../config/config";
import { getDesktopAccessToken, getDesktopApiBaseUrl, ensureValidToken } from "../auth/desktopAuth";

axios.defaults.withCredentials = true;

/**
 * Extract community part from domain (3rd part from the end)
 * Example: "ExampleSiteName.community.diligentoneplatform.com" -> "community"
 * Example: "ExampleSiteName.community-ca.diligentoneplatform.com" -> "community-ca"
 * @param {string} domain - The domain to parse
 * @returns {string|null} - The community part or null if not found
 */
const extractCommunityFromDomain = (domain) => {
  try {
    if (!domain) return null;
    
    // Handle full URL format (https://domain.com) or just domain
    let hostname = domain;
    if (domain.startsWith('https://') || domain.startsWith('http://')) {
      try {
        const url = new URL(domain);
        hostname = url.hostname;
      } catch (urlError) {
        console.warn("[COMMUNITY-EXTRACT] ⚠️ Error parsing URL, using domain as-is:", urlError.message);
      }
    }
    
    const parts = hostname.split('.');
    
    // We need at least 3 parts: subdomain.community.platform.extension
    if (parts.length < 3) {
      return null;
    }
    
    // Get the 3rd part from the end (community part)
    const communityPart = parts[parts.length - 3];
    
    return communityPart;
  } catch (error) {
    console.error("[COMMUNITY-EXTRACT] ❌ Error extracting community:", error.message);
    return null;
  }
};

/**
 * Build correct addon URL with the ancestor community
 * @param {string} ancestorDomain - The ancestor domain from SharePoint
 * @param {string} currentHostname - The current addon hostname
 * @param {string} ancestorCommunity - The community part from ancestor domain
 * @returns {string} - The correct addon URL
 */
const buildCorrectAddonUrl = (ancestorDomain, currentHostname, ancestorCommunity) => {
  try {
    // Parse current hostname to get platform and extension
    const currentParts = currentHostname.split('.');
    
    if (currentParts.length < 3) {
      console.error("[ADDON-URL] ❌ Invalid current hostname structure:", currentHostname);
      return window.location.href; // Fallback to current URL
    }
    
    // Extract platform and extension from current hostname
    const platform = currentParts[currentParts.length - 2];
    const extension = currentParts[currentParts.length - 1];
    
    // Build correct addon URL with ancestor community
    const correctAddonUrl = `https://diligentaddon.${ancestorCommunity}.${platform}.${extension}`;
    
    return correctAddonUrl;
  } catch (error) {
    console.error("[ADDON-URL] ❌ Error building addon URL:", error.message);
    return window.location.href; // Fallback to current URL
  }
};

/**
 * Determine API base URL from ancestorOrigins, then communitydomain cookie
 * No localhost fallback - will throw error if domain cannot be determined
 */
let API_BASE_URL;
try {
  if (
    window.location &&
    window.location.ancestorOrigins &&
    window.location.ancestorOrigins.length > 1
  ) {
    API_BASE_URL = window.location.ancestorOrigins[1];
  } else {
    // Check if communitydomain is available in localStorage (from dialog cookie)
    const communityDomain = localStorage.getItem("communitydomain");
    if (communityDomain) {
      API_BASE_URL = communityDomain;
    } else {
      // No fallback - this will be handled dynamically in getApiConfig
      API_BASE_URL = null;
    }
  }
} catch (err) {
  console.error("[API-URL] Error during initialization:", err);
  // Check if communitydomain is available in localStorage (from dialog cookie)
  const communityDomain = localStorage.getItem("communitydomain");
  if (communityDomain) {
    API_BASE_URL = communityDomain;
  } else {
    // No fallback - this will be handled dynamically in getApiConfig
    API_BASE_URL = null;
  }
}

export { API_BASE_URL, getApiConfig, isDesktopMode };

/**
 * Attempt to infer the current WOPI host/domain from ancestor origins
 * Returns a URL string or null if unavailable
 */
const getAncestorDomainHint = () => {
  try {
    if (
      window.location &&
      window.location.ancestorOrigins &&
      window.location.ancestorOrigins.length > 1
    ) {
      return window.location.ancestorOrigins[1];
    }
  } catch (e) {
    // ignore
  }
  return null;
};

/**
 * Extract hostname from a URL-like string safely
 */
const extractHostname = (urlLike) => {
  try {
    const u = new URL(urlLike);
    return u.hostname.toLowerCase();
  } catch (e) {
    // Fallback: try to strip protocol manually
    try {
      const withoutProto = urlLike.replace(/^https?:\/\//i, "");
      return withoutProto.split("/")[0].toLowerCase();
    } catch (_) {
      return null;
    }
  }
};

/**
 * Check if we're in OIDC authentication mode (Desktop or SharePoint)
 * @returns {boolean} - True if OIDC mode (Desktop/SharePoint) with valid token
 */
const isOidcMode = () => {
  try {
    // First check if we have WOPI tokens (addonkey/communitydomain)
    // If we do, we're in WOPI mode regardless of UserData presence
    const addonkey = localStorage.getItem("addonkey");
    const communitydomain = localStorage.getItem("communitydomain");
    
    if (addonkey || communitydomain) {
      console.log("[AUTH-MODE] WOPI tokens detected, using WOPI mode");
      return false; // Force WOPI mode
    }
    
    // Check if we have OIDC token in UserData (used by both Desktop and SharePoint)
    const userData = localStorage.getItem("UserData");
    const isUserLoggedIn = localStorage.getItem("IsUserLoggedIn") === "true";
    
    if (userData && isUserLoggedIn) {
      try {
        const parsedUserData = JSON.parse(userData);
        const hasOidcToken = !!(parsedUserData && parsedUserData.access_token);
        console.log("[AUTH-MODE] OIDC mode check (Desktop/SharePoint):", hasOidcToken);
        return hasOidcToken;
      } catch (parseError) {
        console.log("[AUTH-MODE] Error parsing UserData:", parseError);
        return false;
      }
    }
    
    console.log("[AUTH-MODE] No OIDC authentication found");
    return false;
  } catch (error) {
    console.log("[AUTH-MODE] Error in mode detection:", error);
    return false;
  }
};

// Keep legacy function name for backward compatibility
const isDesktopMode = isOidcMode;

/**
 * Get API configuration based on authentication mode
 * @returns {Object} - { baseUrl, headers }
 */
const getApiConfig = async () => {
  if (isDesktopMode()) {
    // OIDC mode (Desktop/SharePoint): use subdomain-based URL and OIDC Bearer token
    // Ensure we have a valid token (refresh if needed)
    const token = await ensureValidToken();
    
    if (!token) {
      console.error("[API-CONFIG] ❌ Failed to get valid OIDC token for Desktop/SharePoint mode");
      throw new Error("Authentication failed - please sign in again");
    }
    
    // Debug: Log token details for troubleshooting
    console.log("[API-CONFIG] 🔍 OIDC Token Debug:");
    console.log("[API-CONFIG] - Token length:", token.length);
    console.log("[API-CONFIG] - Token starts with:", token.substring(0, 10));
    console.log("[API-CONFIG] - Token ends with:", token.substring(token.length - 10));
    
    let baseUrl;
    try {
      baseUrl = getDesktopApiBaseUrl();
      console.log("[API-CONFIG] Using OIDC mode (Desktop/SharePoint) - URL:", baseUrl);
    } catch (error) {
      console.error("[API-CONFIG] ❌ Failed to get desktop API URL:", error.message);
      throw new Error(`Configuration Error: ${error.message}. Please sign out and sign in again.`);
    }
    
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    
    console.log("[API-CONFIG] 🔍 Request Headers:", { 
      'Authorization': `Bearer ${token.substring(0, 20)}...`,
      'Content-Type': 'application/json'
    });
    
    return {
      baseUrl: baseUrl,
      headers: headers
    };
  } else {
    // WOPI mode: re-check communitydomain in case it was set after module load
    const communityDomain = localStorage.getItem("communitydomain");
    let wopiBaseUrl = API_BASE_URL;
    
    if (communityDomain) {
      wopiBaseUrl = communityDomain;
      console.log("[API-CONFIG] Using WOPI mode - communitydomain from localStorage:", wopiBaseUrl);
    } else if (API_BASE_URL) {
      console.log("[API-CONFIG] Using WOPI mode - original URL:", wopiBaseUrl);
    } else {
      // No domain available yet - this will be retried after dialog completes
      console.log("[API-CONFIG] ⏳ No domain available yet - WOPI dialog will provide it");
      const errorMsg = "WOPI domain not yet available - dialog required";
      throw new Error(errorMsg);
    }
    
    return {
      baseUrl: wopiBaseUrl,
      headers: {
        'Content-Type': 'application/json'
      }
    };
  }
};



/**
 * Axios request interceptor:
 * Only attach addonkey token for WOPI mode - OIDC mode (Desktop/SharePoint) sets its own Authorization header
 */
axios.interceptors.request.use((config) => {
  // Don't override Authorization header if it's already set (OIDC mode)
  if (!config.headers.Authorization) {
    const token = localStorage.getItem("addonkey");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log("[AXIOS-INTERCEPTOR] Using addonkey token for WOPI mode");
    }
  } else {
    console.log("[AXIOS-INTERCEPTOR] Authorization header already set (OIDC mode), skipping addonkey");
  }
  return config;
});


/**
 * Validate WOPI token by making a test API call
 * @param {string} token - The WOPI token to validate
 * @returns {Promise<boolean>} - True if token is valid, false otherwise
 */
const validateWopiToken = async (token) => {
  try {
    if (!token) {
      console.log("[TOKEN-VALIDATION] ❌ No token provided");
      return false;
    }

    // Get the API base URL for validation
    const communityDomain = localStorage.getItem("communitydomain");
    if (!communityDomain) {
      console.log("[TOKEN-VALIDATION] ❌ No community domain available");
      return false;
    }

    console.log("[TOKEN-VALIDATION] 🔍 Validating token against:", communityDomain);
    
    // Make a lightweight test API call to validate the token
    const response = await axios.get(`${communityDomain}/api/LegalReferences?settings=false`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      withCredentials: true,
      timeout: 5000 // 5 second timeout for validation
    });

    const isValid = response.status >= 200 && response.status < 300;
    console.log("[TOKEN-VALIDATION] ✅ Token validation result:", isValid ? "VALID" : "INVALID");
    return isValid;

  } catch (error) {
    console.log("[TOKEN-VALIDATION] ❌ Token validation failed:", error.message);
    
    // Check if it's a 403/401 error (token expired/invalid)
    if (error.response && (error.response.status === 403 || error.response.status === 401)) {
      console.log("[TOKEN-VALIDATION] ❌ Token is expired/invalid (403/401)");
      return false;
    }
    
    // For other errors (network, timeout), assume token might be valid
    console.log("[TOKEN-VALIDATION] ⚠️ Network/timeout error, assuming token is valid");
    return true;
  }
};

/**
 * Storage key for the policy GUID from cookie
 */
const POLICY_GUID_KEY = "currentPolicyGuid";

/**
 * Ensure token is set up before making API calls using Office Dialog
 * This should be called before any API requests
 */
export const ensureTokenSetup = async () => {
  const existingToken = localStorage.getItem("addonkey");
  const existingDomain = localStorage.getItem("communitydomain");
  const existingPolicyGuid = sessionStorage.getItem(POLICY_GUID_KEY);
  const ancestorHint = getAncestorDomainHint();
  const ancestorHost = ancestorHint ? extractHostname(ancestorHint) : null;
  const storedHost = existingDomain ? extractHostname(existingDomain) : null;
  
  console.log("[TOKEN-SETUP] Initial localStorage check:");
  console.log("[TOKEN-SETUP] - addonkey:", existingToken ? `EXISTS (${existingToken.length} chars)` : "NO TOKEN");
  console.log("[TOKEN-SETUP] - communitydomain:", existingDomain || "NO DOMAIN");
  console.log("[TOKEN-SETUP] - policyGuid:", existingPolicyGuid || "NO POLICY GUID");
  console.log("[TOKEN-SETUP] Current window location:", window.location.href);
  if (ancestorHost) {
    console.log("[TOKEN-SETUP] Ancestor domain hint:", ancestorHint, "(host:", ancestorHost, ")");
  }
  
  // If the stored community domain does not match the current ancestor domain hint,
  // invalidate cached WOPI token/domain to force a fresh dialog for the new site
  if (existingToken && storedHost && ancestorHost && storedHost !== ancestorHost) {
    console.log(
      "[TOKEN-SETUP] ❗ Domain mismatch detected (stored:",
      storedHost,
      "current:",
      ancestorHost,
      ") - clearing cached WOPI token, domain, and policyGuid"
    );
    localStorage.removeItem("addonkey");
    localStorage.removeItem("communitydomain");
    sessionStorage.removeItem(POLICY_GUID_KEY);
  } else if (existingToken && storedHost && ancestorHost && storedHost === ancestorHost) {
    console.log(
      "[TOKEN-SETUP] ✅ Domain match detected (stored:",
      storedHost,
      "current:",
      ancestorHost,
      ") - keeping existing token"
    );
  }
  
  // Re-check after potential invalidation
  const hasValidToken = !!localStorage.getItem("addonkey");
  const ancestorAvailable = !!ancestorHost; // Firefox: ancestorOrigins may be unavailable
  
  // NOTE: PolicyGuid is now primarily read from document custom properties (injected by backend).
  // localStorage policyGuid is kept as a fallback only.
  // We no longer need to aggressively clear/refresh policyGuid via dialog since the
  // document-specific custom property is the reliable source for multi-document scenarios.
  console.log("[TOKEN-SETUP] 🔄 PolicyGuid primary source is now document custom properties (backend-injected)");
  
  if (hasValidToken && ancestorAvailable) {
    console.log("[TOKEN-SETUP] ✅ Token already exists and ancestor domain available, validating token...");
    
    // Validate token before potentially reusing it
    try {
      const token = localStorage.getItem("addonkey");
      const isValid = await validateWopiToken(token);
      
      if (isValid) {
        console.log("[TOKEN-SETUP] ✅ Token is valid, reusing existing token and domain");
        // Token and domain are valid - no need to reopen dialog just for policyGuid
        // PolicyGuid will be read from document custom properties by legalReferenceService
        return;
      } else {
        console.log("[TOKEN-SETUP] ❌ Token is invalid/expired, clearing all and opening dialog");
        localStorage.removeItem("addonkey");
        localStorage.removeItem("communitydomain");
        sessionStorage.removeItem(POLICY_GUID_KEY);
      }
    } catch (error) {
      console.log("[TOKEN-SETUP] ❌ Token validation failed, clearing all and opening dialog:", error.message);
      localStorage.removeItem("addonkey");
      localStorage.removeItem("communitydomain");
      sessionStorage.removeItem(POLICY_GUID_KEY);
    }
  }
  if (hasValidToken && !ancestorAvailable) {
    console.log("[TOKEN-SETUP] ⚠️ Ancestor domain unavailable (likely Firefox). Forcing dialog to refresh WOPI cookies for current site.");
  }

  // For WOPI environments, validate community domain before opening dialog
  if (isDesktopMode() === false) { // WOPI environment
    console.log("[TOKEN-SETUP] 🔍 WOPI environment detected - validating community domain...");
    
    try {
      // Get ancestor domain (community domain from SharePoint)
      const ancestorOrigins = window.location.ancestorOrigins;
      if (ancestorOrigins && ancestorOrigins.length > 1) {
        const ancestorDomain = ancestorOrigins[1];
        const currentHostname = window.location.hostname;
        console.log("[TOKEN-SETUP] 🌐 Ancestor domain (community):", ancestorDomain);
        console.log("[TOKEN-SETUP] 🏠 Current window hostname:", currentHostname);
        
        // Extract community part from both domains
        const ancestorCommunity = extractCommunityFromDomain(ancestorDomain);
        const currentCommunity = extractCommunityFromDomain(currentHostname);
        
        console.log("[TOKEN-SETUP] 🔍 Ancestor community:", ancestorCommunity);
        console.log("[TOKEN-SETUP] 🔍 Current community:", currentCommunity);
        
        // Check if communities match
        if (ancestorCommunity && currentCommunity && ancestorCommunity !== currentCommunity) {
          console.log("[TOKEN-SETUP] ❗ Community mismatch detected - redirecting to correct addon URL");
          
          // Build correct addon URL with ancestor community
          const correctAddonUrl = buildCorrectAddonUrl(ancestorDomain, currentHostname, ancestorCommunity);
          console.log("[TOKEN-SETUP] 🔄 Redirecting to:", correctAddonUrl);
          
          // Redirect to correct addon URL
          window.location.href = correctAddonUrl;
          return; // Exit early, don't open dialog
        } else {
          console.log("[TOKEN-SETUP] ✅ Community domains match - proceeding with dialog");
        }
      } else {
        console.log("[TOKEN-SETUP] ⚠️ No community ancestor origin available (need index 1) - proceeding with dialog");
      }
    } catch (error) {
      console.error("[TOKEN-SETUP] ❌ Error validating community domain:", error.message);
      console.log("[TOKEN-SETUP] 🔄 Proceeding with dialog despite validation error");
    }
  }

  console.log("[TOKEN-SETUP] Opening dialog for token...");
  
  try {
    await new Promise((resolve, reject) => {
      Office.context.ui.displayDialogAsync(
        `${window.location.origin}/dialogue.html`,
        // `${window.location.origin}/dialogue.html`,   //for local
        { height: 50, width: 50 },
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
            const dialog = asyncResult.value;
            console.log("[TOKEN-SETUP] ✅ Dialog opened");

            dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
              console.log("[TOKEN-SETUP] Message received from dialog");
              console.log("[TOKEN-SETUP] Raw message:", arg.message);
              console.log("[TOKEN-SETUP] Message type:", typeof arg.message);
              
              try {
                const message = JSON.parse(arg.message);
                
                if (message.type === 'TOKEN_EXTRACTED' && message.token) {
                  console.log("[TOKEN-SETUP] Token extracted, storing in localStorage immediately...");
                  
                  try {
                    localStorage.setItem("addonkey", message.token);
                    
                    // Also store community domain if provided
                    if (message.communityDomain) {
                      localStorage.setItem("communitydomain", message.communityDomain);
                      console.log("[TOKEN-SETUP] Community domain stored:", message.communityDomain);
                    }
                    
                    // Store policyGuid in sessionStorage as fallback (primary source is document custom properties)
                    // sessionStorage is tab-scoped, so it provides better multi-document isolation than localStorage
                    if (message.policyGuid) {
                      sessionStorage.setItem(POLICY_GUID_KEY, message.policyGuid);
                      console.log("[TOKEN-SETUP] ✅ Policy GUID stored in sessionStorage (fallback):", message.policyGuid);
                      console.log("[TOKEN-SETUP] ℹ️ Primary policyGuid source is document custom properties (backend-injected)");
                    } else {
                      console.log("[TOKEN-SETUP] ⚠️ No policyGuid from dialog (will rely on document custom properties)");
                    }
                    
                    // Verify storage was successful
                    const storedToken = localStorage.getItem("addonkey");
                    if (storedToken === message.token) {
                      console.log("[TOKEN-SETUP] ✅ Token successfully stored and verified");
                      console.log("[TOKEN-SETUP] Token length in storage:", storedToken.length);
                    } else {
                      console.error("[TOKEN-SETUP] ❌ Token storage verification failed - tokens don't match");
                      console.error("[TOKEN-SETUP] Expected:", message.token.substring(0, 20) + "...");
                      console.error("[TOKEN-SETUP] Got:", storedToken ? storedToken.substring(0, 20) + "..." : "null");
                    }
                  } catch (storageError) {
                    console.error("[TOKEN-SETUP] ❌ Error storing token:", storageError);
                  }
                  
                  // Don't close dialog yet - wait for setup completion message
                  console.log("[TOKEN-SETUP] Waiting for token setup completion...");
                  
                } else if (message.type === 'TOKEN_SETUP_COMPLETE') {
                  console.log("[TOKEN-SETUP] Token setup completed, closing dialog");
                  console.log("[TOKEN-SETUP] Cookie deletion will be handled after successful getPolicyData() call");
                  
                  // Now we can close the dialog and resolve
                  dialog.close();
                  resolve();
                  
                } else if (message.type === 'NO_TOKEN') {
                  console.log("[TOKEN-SETUP] ❌ No token found in dialog");
                  console.log("[TOKEN-SETUP] Dialog domain:", message.domain || "unknown");
                  console.log("[TOKEN-SETUP] Available cookies in dialog:", message.availableCookies || "none");
                  dialog.close();
                  resolve();
                } else {
                  console.log("[TOKEN-SETUP] ❌ Unknown message type:", message.type);
                }
                
              } catch (parseError) {
                // Fallback for non-JSON messages (backward compatibility)
                const token = arg.message;
                if (token && token.trim() !== "") {
                  localStorage.setItem("addonkey", token);
                  console.log("[TOKEN-SETUP] ✅ Token stored (legacy format)");
                }
                dialog.close();
                resolve();
              }
            });

            dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
              if (arg.error) {
                console.error("[TOKEN-SETUP] ❌ Dialog error:", arg.error);
                dialog.close();
                reject(new Error(arg.error));
              }
            });
          } else {
            console.error("[TOKEN-SETUP] ❌ Failed to create dialog:", asyncResult.error?.message);
            reject(new Error(asyncResult.error?.message));
          }
        }
      );
    });
    
    console.log("[TOKEN-SETUP] ✅ Dialog setup completed");
    
  } catch (error) {
    console.error("[TOKEN-SETUP] ❌ Error:", error.message);
    console.log("[TOKEN-SETUP] Dialog failed, no fallback available");
  }
};

/**
 * Retrieves all policy books with their GUIDs.
 */
export const getAllPolicyBooks = async () => {
  try {
    const apiConfig = await getApiConfig();
    const response = await axios.get(`${apiConfig.baseUrl}/api/policies/publishedList`, {
      headers: apiConfig.headers,
      ...(isDesktopMode() ? {} : { withCredentials: true })
    });
    console.log("[GET-POLICY-BOOKS] Found", response.data.length, "policy books");
    
    // Return all books with their full data structure
    return response.data.map(book => ({
      guid: book.guid,
      title: book.title,
      code: book.code || "",
      canView: book.canView,
      folder: book.folder || false,
      hasChildren: book.hasChildren || false,
      isBook: book.isBook || false,
      isPublic: book.isPublic || false,
    }));
  } catch (error) {
    console.error("Error fetching policy books:", error);
    throw error;
  }
};

/**
 * Lazy loads children of a policy folder/book.
 * Uses the same API pattern as getPoliciesByBookGuid: /api/policies/publishedList?id={guid}
 * @param {string} parentId - The GUID of the parent folder/book
 * @returns {Promise<Array>} - Array of child policies/folders
 */
export const getPolicyChildren = async (parentId) => {
  try {
    const apiConfig = await getApiConfig();
    const response = await axios.get(`${apiConfig.baseUrl}/api/policies/publishedList?id=${parentId}`, {
      headers: apiConfig.headers,
      ...(isDesktopMode() ? {} : { withCredentials: true })
    });

    if (!response.data || response.data.length === 0) {
      console.log("[GET-POLICY-CHILDREN] No children found for:", parentId);
      return [];
    }

    console.log("[GET-POLICY-CHILDREN] Found", response.data.length, "children for:", parentId);

    // Transform the response to include all necessary fields
    return response.data.map((item) => ({
      guid: item.guid,
      title: item.title,
      code: item.code || "",
      folder: item.folder || false,
      hasChildren: item.hasChildren || false,
      isBook: item.isBook || false,
      canView: item.canView || false,
      isPublic: item.isPublic || false,
    }));
  } catch (error) {
    console.error(`Error fetching children for policy ${parentId}:`, error);
    throw error;
  }
};

/**
 * Recursively retrieves policies from a specific book/folder GUID, handling N-level hierarchy.
 */
export const getPoliciesByBookGuid = async (bookGuid, bookTitle, level = 0) => {
  const indent = "  ".repeat(level); // Indentation for logging hierarchy
  
  try {
    console.log(`${indent}[GET-POLICIES] Level ${level}: Fetching content for: ${bookTitle}`);
    const apiConfig = await getApiConfig();
    const response = await axios.get(`${apiConfig.baseUrl}/api/policies/publishedList?id=${bookGuid}`, {
      headers: apiConfig.headers,
      ...(isDesktopMode() ? {} : { withCredentials: true })
    });
    
    if (!response.data || response.data.length === 0) {
      console.log(`${indent}[GET-POLICIES] Level ${level}: No content found in: ${bookTitle}`);
      return [];
    }
    
    let allPolicies = [];
    
    // Separate folders and actual policies (service now returns only published policies)
    const folders = response.data.filter(item => item.folder === true);
    const actualPolicies = response.data.filter(item => item.folder === false);
    
    // Add actual policies from current level
    if (actualPolicies.length > 0) {
      const policies = actualPolicies.map(policy => ({
        title: policy.title,
        guid: policy.guid,
        code: policy.code,
        bookTitle: bookTitle, // Add book title for reference
        level: level, // Track hierarchy level
        isPublic: policy.isPublic || false, // Include isPublic for URL generation
      }));
      
      allPolicies.push(...policies);
      console.log(`${indent}[GET-POLICIES] Level ${level}: Found ${policies.length} actual policies in: ${bookTitle}`);
    }
    
    // Recursively process subfolders
    if (folders.length > 0) {
      console.log(`${indent}[GET-POLICIES] Level ${level}: Found ${folders.length} subfolders in: ${bookTitle}`);
      
      for (const folder of folders) {
        console.log(`${indent}[GET-POLICIES] Level ${level}: Processing subfolder: ${folder.title}`);
        
        // Recursive call for each subfolder
        const subPolicies = await getPoliciesByBookGuid(
          folder.guid, 
          `${bookTitle}/${folder.title}`, // Build hierarchy path
          level + 1 // Increment level
        );
        
        allPolicies.push(...subPolicies);
      }
    }
    
    console.log(`${indent}[GET-POLICIES] Level ${level}: Total policies collected from ${bookTitle}: ${allPolicies.length}`);
    return allPolicies;
    
  } catch (error) {
    console.error(`${indent}Error fetching content for ${bookTitle} at level ${level}:`, error);
    return []; // Return empty array instead of throwing
  }
};

/**
 * Search published policies via backend API (/api/items/search).
 * Uses same authentication as other policy APIs (WOPI/SharePoint/Desktop).
 * Response may be { result: { results: [...] } } or { results: [...] }; list is taken from `results`.
 * @param {string} searchKey - The search term (keyWords)
 * @returns {Promise<Array>} - Array of policy items (guid, title, code, isPublic, etc.)
 */
export const searchPublishedPolicies = async (searchKey) => {
  if (!searchKey || typeof searchKey !== 'string' || !searchKey.trim()) {
    return [];
  }

  try {
    const apiConfig = await getApiConfig();
    const trimmed = searchKey.trim();
    const params = new URLSearchParams({
      keyWords: trimmed,
      includeTotals: "true",
      searchTypes: "Policy",
      folderTypes: "6",
      PublishPolicySearch: "true",
    });
    const response = await axios.get(
      `${apiConfig.baseUrl}/api/items/search?${params.toString()}`,
      {
        headers: apiConfig.headers,
        ...(isDesktopMode() ? {} : { withCredentials: true })
      }
    );

    const data = response.data;
    if (!data || typeof data !== "object") {
      console.log("[SEARCH-POLICIES] No payload for:", searchKey);
      return [];
    }

    const wrapped =
      (data.result != null && typeof data.result === "object" && data.result) ||
      (data.Result != null && typeof data.Result === "object" && data.Result) ||
      null;
    const payload = wrapped || data;
    const rawResults = Array.isArray(payload.results)
      ? payload.results
      : Array.isArray(payload.Results)
        ? payload.Results
        : [];

    if (rawResults.length === 0) {
      console.log("[SEARCH-POLICIES] No results for:", searchKey);
      return [];
    }

    // Normalize to policy item shape (guid, title, code, isPublic, folder: false)
    return rawResults
      .filter(
        (item) =>
          item &&
          item.guid &&
          item.folder !== true &&
          item.policy !== false
      )
      .map((item) => ({
        guid: item.guid,
        title: item.title,
        code: item.code || "",
        folder: false,
        isPublic: !!item.isPublic,
        canView: item.canView !== undefined ? !!item.canView : true,
      }));
  } catch (error) {
    console.error("[SEARCH-POLICIES] Error searching policies:", error);
    throw error;
  }
};

/**
 * Retrieves all policies from all policy books.
 */
export const getAllPolicies = async () => {
  try {
    console.log("[GET-ALL-POLICIES] Starting to fetch all policies...");
    
    // Step 1: Get all policy books
    const policyBooks = await getAllPolicyBooks();
    console.log("[GET-ALL-POLICIES] Found", policyBooks.length, "policy books");
    
    // Step 2: Get policies from each book
    const allPoliciesPromises = policyBooks.map(book => 
      getPoliciesByBookGuid(book.guid, book.title)
    );
    
    // Step 3: Wait for all requests to complete
    const allPoliciesArrays = await Promise.all(allPoliciesPromises);
    
    // Step 4: Combine all policies from all books (skip empty arrays)
    const allPolicies = allPoliciesArrays
      .filter(policies => policies.length > 0) // Skip empty responses
      .flat(); // Flatten array of arrays
    
    console.log("[GET-ALL-POLICIES] ✅ Total policies found:", allPolicies.length);
    return allPolicies;
    
  } catch (error) {
    console.error("Error fetching all policies:", error);
    throw error;
  }
};

/**
 * Fetches raw policy data (used for session validation).
 */
export const getPolicyData = async () => {
  console.log("[POLICY-DATA] Starting fetch...");
  
  try {
    if (isDesktopMode()) {
      const apiConfig = await getApiConfig();
      console.log("[POLICY-DATA] OIDC mode (Desktop/SharePoint) - using OIDC Bearer token");
      
      const response = await axios.get(`${apiConfig.baseUrl}/api/LegalReferences?settings=false`, {
        headers: apiConfig.headers,
      });
      
      console.log("[POLICY-DATA] ✅ Desktop Response:", response.status);
      
      return {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        data: response.data,
        statusText: response.statusText,
      };
      
    } else {
      console.log("[POLICY-DATA] WOPI mode - using existing token setup");
      
      // Ensure dialog runs first so communitydomain/addonkey are fresh
      await ensureTokenSetup();
      
      // Recompute API config AFTER dialog updates localStorage
      const apiConfig = await getApiConfig();
      const token = localStorage.getItem("addonkey");
      console.log("[POLICY-DATA] Token ready:", token ? "YES" : "NO");
      
      const response = await axios.get(`${apiConfig.baseUrl}/api/LegalReferences?settings=false`, {
        headers: apiConfig.headers,
        withCredentials: true,
      });
      
      console.log("[POLICY-DATA] ✅ WOPI Response:", response.status);
      
      return {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        data: response.data,
        statusText: response.statusText,
      };
    }
    
  } catch (error) {
    console.error("[POLICY-DATA] ❌ Error:", error.message);
    
    if (error.response) {
      console.log("[POLICY-DATA] ❌ API error status:", error.response.status);
      return {
        ok: false,
        status: error.response.status,
        data: error.response.data,
        statusText: error.response.statusText,
      };
    }
    
    throw error;
  }
};
